import { ChangeEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { AccomodationsTable } from './AccommodationsTable/AccommodationsTable';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination } from '~/ui';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import '~/assets/css/select-table.css';
import styles from './AccommodationsBoard.module.scss';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { getPaginate } from '~/utils/getPaginate';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';

interface SelectedPage {
  selected: number;
}

interface FoodPoints {
  page: number;
}

export const AccommodationsBoard = () => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<FoodPoints>({
      initialValues: { page: 15 },
      key: 'accommondation-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const toastError = useToastError();
  const client = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
  };

  const {
    data: accommodations,
    isError,
    isLoading: isDataLoading,
    error,
  } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/accommodations', {
        params,
      });
      return response.data;
    },
    queryKey: ['accommodations', params],
    keepPreviousData: true,
  });

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredFoods = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredFoods);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === accommodations?.data.length) {
      setSelectedItems([]);
    } else {
      const foodIdArray = accommodations?.data.map(
        (accomodation: any) => accomodation.id
      );
      setSelectedItems(foodIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/accommodations-many', {
        data: { accommodation_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['accommodations']);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    setPage(paginate);
  }, [paginate]);

  return (
    <Board>
      <div className={styles.accommodationsTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('search_by_id_np')}
            classNameInput={styles.accommodationsTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.accommodationsTableInputWrapper}
          />
        </div>
      </div>
      <PageTransition page={page ?? paginate}>
        {isError ? (
          <ErrorMessage error={error} />
        ) : (
          <AccomodationsTable
            accommodations={accommodations?.data || []}
            sort={sort}
            setSort={setSort}
            handleToggleAll={handleToggleAll}
            handleSelect={handleSelect}
            selectedItems={selectedItems}
            isLoading={isDataLoading}
          />
        )}
      </PageTransition>
      <div className={styles.accommodationsTablePagination}>
        <TableAmountPage
          firstRow={accommodations?.from}
          lastRow={accommodations?.to}
          total={accommodations?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={accommodations?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
